import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {CaretDown} from '../../icons';
import {LearnTeamApi} from '../../../api/learnTeamApi';
import {isOk} from '../../../utils/apiResponse';
import {relativeTimeLabel} from '../../client/panel_v11/panelFormatters';
import {SortableTh} from '../../elements/SortableTh';
import {nextSort, sortRows} from '../../../utils/sortRows';
import {
    canRemind,
    hasDetail,
    invitationFor,
    invitationsByEmail,
    isInactive,
    memberSortValue,
    membersByUserId,
    progressPercent,
    remindedRecently,
    shortDate,
} from './teamLearningModel';
import './TeamLearning.scss';

/**
 * El avance del equipo en «Aprende Dashbo», dentro de Usuarios (`/agency-team`, sólo en la 2.1).
 *
 * Se monta SÓLO si `GET learning/team/` respondió OK, que el backend reserva a los Admin: el
 * componente no decide el rol de nadie. Sin ese dato la pantalla queda como siempre (la tabla
 * clásica de `AgencyTeam`).
 *
 * Reglas de la sección 7 del mockup aprobado, para que no se sienta vigilancia:
 *  - avance y logros, nunca contenido: «Consultas IA» cuenta cuántas, nunca qué se preguntó;
 *  - sin ranking POR DEFECTO: las filas van en el orden de `agency/team`. El Admin puede ordenar a
 *    pedido tocando el encabezado de una columna de avance (card Ni3pOCBx, fede 08/10: «no me deja
 *    ordenar por cantidad de consultas para ver quiénes son los que más lo usan»). El tercer clic
 *    vuelve al orden de `agency/team`;
 *  - la persona ve lo mismo en su propia página (eso lo dice `/aprende`, no esta pantalla).
 */

// Las columnas de avance. Estas MISMAS claves alimentan el encabezado y el `data-label` de cada
// celda, que es lo que el CSS muestra como rótulo cuando la tabla angosta pasa a tarjetas: una
// columna no puede llamarse distinto según el ancho (mismo criterio que `TeamCard`).
const LEARNING_COLUMNS = {
    lastUse: 'agency_team.learning.col.last_use',
    essentials: 'agency_team.learning.col.essentials',
    profile: 'agency_team.learning.col.profile',
    achievements: 'agency_team.learning.col.achievements',
    aiQueries: 'agency_team.learning.col.ai_queries',
};
const LEARNING_COLUMN_COUNT = Object.keys(LEARNING_COLUMNS).length;

// i18next devuelve la clave cuando no la encuentra: una tarea, un perfil o un logro que el bundle no
// conoce no se dibuja (mismo contrato que el drawer de primeros pasos).
function knownText(t, key, options) {
    const text = t(key, options);
    return text && text !== key ? text : null;
}

const taskTitle = (t, key) => knownText(t, `learn_dashbo.tasks.${key}.title`);

/**
 * El email con un punto de corte antes de la «@»: en una columna angosta pasa a dos renglones
 * («kanka.agencia» / «@gmail.com») en vez de partirse en medio del dominio (QA-VIS-apr-eq-02).
 */
export function breakableEmail(email) {
    const at = typeof email === 'string' ? email.indexOf('@') : -1;
    if (at <= 0) return email;
    return <>{email.slice(0, at)}<wbr/>{email.slice(at)}</>;
}

/** Subtítulo + fila de resumen, debajo del encabezado de la caja. */
export function TeamLearningHead({summary}) {
    const {t} = useTranslation();
    if (!summary) return null;
    const people = Number(summary.people) || 0;
    // La clave trae el número adentro («{{count}} personas»): acá el número va aparte y grande, así
    // que se usa sólo el sustantivo, ya en su plural.
    const peopleLabel = String(t('agency_team.learning.summary.people', {count: people}))
        .replace(/^\s*\d+\s*/, '');
    return (
        <div className="atl-head">
            <p className="atl-head__sub">{t('agency_team.learning.subtitle')}</p>
            <div className="atl-summary" data-testid="atl-summary">
                <SummaryChip value={people} label={peopleLabel}/>
                <SummaryChip value={Number(summary.essentials_done) || 0}
                             label={t('agency_team.learning.summary.essentials_done')}/>
                <SummaryChip value={Number(summary.inactive_7d) || 0}
                             label={t('agency_team.learning.summary.inactive')}/>
            </div>
        </div>
    );
}

function SummaryChip({value, label}) {
    return (
        <div className="atl-sum">
            <strong>{value}</strong>
            <span>{label}</span>
        </div>
    );
}

/** La barra con su «2 de 6». */
function ProgressBar({progress, label, t}) {
    return (
        <span className="atl-prog">
            <span className="atl-bar" aria-hidden="true">
                <span className="atl-bar__fill" style={{width: `${progressPercent(progress)}%`}}/>
            </span>
            <span className="atl-prog__text">
                {label && <span className="atl-prog__label">{label} </span>}
                <b>{t('learn_dashbo.track.progress', {done: progress.done, total: progress.total})}</b>
            </span>
        </span>
    );
}

/**
 * La tabla del equipo con las columnas de avance. Las operaciones de cada fila (editar, borrar) y el
 * rol las sigue dibujando `AgencyTeam`, que las recibe por render props: son las mismas que en la
 * tabla clásica, y no pueden divergir.
 */
export function TeamLearningTable({
    guideAnchor, team, invitations, learning, showRole, currentUserId,
    renderRole, renderInvitationRole, renderUserOperation, renderInvitationOperation,
}) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(() => new Set());
    // Lo que «Recordarle» supo en esta visita: `{userId: {at, limit}}`. Pisa al `nudged_at` del payload
    // sin recargar la pantalla.
    const [reminders, setReminders] = useState({});
    const [sending, setSending] = useState(null);
    // `{column, direction}`; sin columna, el orden de `agency/team` (ver `utils/sortRows.js`).
    const [sort, setSort] = useState({column: null, direction: null});

    const members = useMemo(() => membersByUserId(learning), [learning]);
    // Los usuarios del portal (tipo Client) no tienen avance: `memberSortValue` da `null` y van al
    // final en las dos direcciones. Las invitaciones no se ordenan: siguen debajo, como siempre.
    const rows = useMemo(() => sortRows(
        team,
        (user) => memberSortValue(user.user_type === 'Client' ? null : members.get(user.id), sort.column),
        sort.column ? sort.direction : null,
    ), [team, members, sort]);
    const onSort = (column, firstDirection) => setSort((prev) => nextSort(prev, column, firstDirection));
    const learningInvitations = invitationsByEmail(learning);
    // Persona + (rol) + las de avance + acciones: el ancho de la fila de detalle.
    const columnCount = 2 + (showRole ? 1 : 0) + LEARNING_COLUMN_COUNT;

    const toggle = (userId) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(userId)) next.delete(userId); else next.add(userId);
        return next;
    });

    const remind = (member) => {
        setSending(member.user_id);
        LearnTeamApi.nudge(member.user_id).then((response) => {
            setSending(null);
            if (isOk(response)) {
                const at = (response.data && response.data.nudged_at) || new Date().toISOString();
                setReminders((prev) => ({...prev, [member.user_id]: {at}}));
                toast.success(t('agency_team.learning.remind_done'));
                return;
            }
            const code = response && (response.error_code || response.status);
            if (code === 'NUDGE_TOO_SOON') {
                // El tope lo puso otro Admin (o esta pestaña quedó vieja): la fila deja de ofrecer el
                // botón. La fecha no se inventa: si el payload no la traía, se dice sin fecha.
                setReminders((prev) => ({...prev, [member.user_id]: {at: member.nudged_at || null, limit: true}}));
                toast.info(t('agency_team.learning.remind_limit'));
                return;
            }
            toast.error(t('agency_team.learning.remind_error'));
        });
    };

    const label = (column) => t(LEARNING_COLUMNS[column]);

    return (
        <div className="atl" {...guideAnchor}>
            <table className="atl-table">
                <thead>
                <tr>
                    <th>{t('agency_team.user')}</th>
                    {showRole && <th>{t('agency_team.role')}</th>}
                    <SortableTh label={label('lastUse')} column="lastUse" sort={sort} onSort={onSort}/>
                    <SortableTh label={label('essentials')} column="essentials" sort={sort} onSort={onSort}/>
                    <SortableTh label={label('profile')} column="profile" sort={sort} onSort={onSort}/>
                    <SortableTh label={label('achievements')} column="achievements" sort={sort} onSort={onSort}/>
                    <SortableTh label={label('aiQueries')} column="aiQueries" sort={sort} onSort={onSort}
                                className="-num"/>
                    <th/>
                </tr>
                </thead>
                <tbody>
                {rows.map((user) => {
                    const member = user.user_type === 'Client' ? null : members.get(user.id);
                    const isOpen = !!member && expanded.has(user.id);
                    return (
                        <React.Fragment key={`user-${user.id}`}>
                            <tr className={`atl-row${isOpen ? ' -open' : ''}`}>
                                <td className="atl-who">
                                    <strong>{user.name}</strong>
                                    <small>{breakableEmail(user.email)}</small>
                                </td>
                                {showRole && <td className="atl-role" data-label={t('agency_team.role')}>{renderRole(user)}</td>}
                                {member
                                    ? <MemberCells member={member} isOpen={isOpen} onToggle={() => toggle(user.id)}
                                                   label={label} t={t}/>
                                    : <td className="atl-none" colSpan={LEARNING_COLUMN_COUNT}/>}
                                <td className="atl-actions">
                                    {member && (
                                        <RemindControl member={member} reminder={reminders[member.user_id]}
                                                       currentUserId={currentUserId}
                                                       sending={sending === member.user_id}
                                                       onRemind={() => remind(member)} t={t}/>
                                    )}
                                    {renderUserOperation(user)}
                                </td>
                            </tr>
                            {isOpen && (
                                <tr className="atl-detail" id={`atl-detail-${user.id}`}>
                                    <td colSpan={columnCount}>
                                        <MemberDetail member={member} t={t}/>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
                {invitations.map((invitation) => (
                    <InvitationRow key={`invitation-${invitation.id}`} invitation={invitation}
                                   learningInvitation={invitationFor(learningInvitations, invitation.email)}
                                   showRole={showRole} renderInvitationRole={renderInvitationRole}
                                   renderInvitationOperation={renderInvitationOperation} t={t}/>
                ))}
                </tbody>
            </table>
            <p className="atl-footnote">{t('agency_team.learning.footnote')}</p>
        </div>
    );
}

// «hoy», «ayer», «hace 9 días» vienen en minúscula del formateador del Panel (van en medio de una
// frase); en una celda suelta arrancan con mayúscula.
function capitalized(text) {
    return text ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : text;
}

function MemberCells({member, isOpen, onToggle, label, t}) {
    const lastUse = capitalized(relativeTimeLabel(member.last_activity_at, t)) || t('client_panel.team_never');
    const expandable = hasDetail(member);
    const essentials = member.essentials || {done: 0, total: 0};
    return (
        <>
            <td className={`atl-last${isInactive(member.last_activity_at) ? ' -stale' : ''}`}
                data-label={label('lastUse')}>
                {lastUse}
            </td>
            <td className="atl-essentials" data-label={label('essentials')}>
                {expandable ? (
                    <button type="button" className="atl-expand" onClick={onToggle}
                            aria-expanded={isOpen} aria-controls={`atl-detail-${member.user_id}`}>
                        <ProgressBar progress={essentials} t={t}/>
                        <CaretDown size={14} className="atl-expand__caret" aria-hidden="true"/>
                    </button>
                ) : <ProgressBar progress={essentials} t={t}/>}
            </td>
            <td className="atl-profile" data-label={label('profile')}>
                <ProfileCell member={member} t={t}/>
            </td>
            <td className="atl-achievements" data-label={label('achievements')}>
                <Achievements keys={member.achievements} t={t}/>
            </td>
            <td className="atl-num" data-label={label('aiQueries')}>
                {Number(member.ai_queries_30d) || 0}
            </td>
        </>
    );
}

function ProfileCell({member, t}) {
    if (!member.profile_chosen) {
        return <span className="atl-muted">{t('agency_team.learning.profile_none')}</span>;
    }
    const tracks = (member.profile_tracks || [])
        .map((track) => ({track, name: knownText(t, `learn_dashbo.profile.${track.key}`)}))
        .filter(({name}) => name);
    if (tracks.length === 0) return <span className="atl-muted">—</span>;
    return (
        <span className="atl-tracks">
            {tracks.map(({track, name}) => (
                <ProgressBar key={track.key} progress={track} label={name} t={t}/>
            ))}
        </span>
    );
}

function Achievements({keys, t}) {
    const names = (keys || [])
        .map((key) => ({key, name: knownText(t, `learn_dashbo.achievements.${key}`)}))
        .filter(({name}) => name);
    if (names.length === 0) return <span className="atl-muted">—</span>;
    return (
        <span className="atl-ach">
            {names.map(({key, name}) => <i key={key}>{name}</i>)}
        </span>
    );
}

function RemindControl({member, reminder, currentUserId, sending, onRemind, t}) {
    const at = reminder ? reminder.at : member.nudged_at;
    if (remindedRecently(at)) {
        return (
            <span className="atl-reminded">
                {t('agency_team.learning.reminded', {date: shortDate(at), interpolation: {escapeValue: false}})}
            </span>
        );
    }
    // El backend dijo que ya tiene un aviso de esta semana, pero no cuándo: se dice sin fecha.
    if (reminder && reminder.limit) {
        return <span className="atl-reminded">{t('agency_team.learning.remind_limit')}</span>;
    }
    if (!canRemind(member, currentUserId)) return null;
    return (
        <button type="button" className="atl-remind" onClick={onRemind} disabled={sending}>
            {t('agency_team.learning.remind')}
        </button>
    );
}

/** Lo que se ve al expandir: qué le falta de lo esencial, lo marcado a mano y el último avance. */
function MemberDetail({member, t}) {
    const pending = (member.pending_essentials || [])
        .map((key) => ({key, title: taskTitle(t, key)}))
        .filter(({title}) => title);
    const manual = (member.manual_done || [])
        .map((key) => ({key, title: taskTitle(t, key)}))
        .filter(({title}) => title);
    const lastProgress = shortDate(member.last_progress_at);
    return (
        <div className="atl-detail__in">
            {pending.length > 0 && (
                <div className="atl-detail__group">
                    <span className="atl-detail__title">{t('agency_team.learning.pending_title')}</span>
                    <ul>{pending.map(({key, title}) => <li key={key}>{title}</li>)}</ul>
                </div>
            )}
            {manual.length > 0 && (
                <div className="atl-detail__group">
                    <ul>
                        {manual.map(({key, title}) => (
                            <li key={key}>
                                {title} <span className="atl-muted">· {t('agency_team.learning.marked_manually')}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {lastProgress && (
                <span className="atl-muted">
                    {t('agency_team.learning.last_progress', {date: lastProgress, interpolation: {escapeValue: false}})}
                </span>
            )}
        </div>
    );
}

/**
 * Una invitación pendiente. Las de equipo dicen «Todavía no entró» y que su avance aparece al
 * aceptar; las del portal no llevan columnas de avance, igual que sus usuarios.
 */
function InvitationRow({invitation, learningInvitation, showRole, renderInvitationRole, renderInvitationOperation, t}) {
    const isTeam = invitation.user_type !== 'Client';
    const sentAt = learningInvitation ? shortDate(learningInvitation.sent_at) : null;
    return (
        <tr className="atl-row -invitation">
            <td className="atl-who">
                <strong>{breakableEmail(invitation.email)}</strong>
                <small>
                    {sentAt
                        ? t('agency_team.learning.invite_sent', {date: sentAt, interpolation: {escapeValue: false}})
                        : t('agency_team.pending_invitation')}
                </small>
            </td>
            {showRole && <td className="atl-role" data-label={t('agency_team.role')}>{renderInvitationRole(invitation)}</td>}
            {isTeam ? (
                <>
                    <td className="atl-muted atl-pending">{t('agency_team.learning.invite_pending')}</td>
                    <td className="atl-muted atl-pending" colSpan={LEARNING_COLUMN_COUNT - 1}>
                        {t('agency_team.learning.invite_progress')}
                    </td>
                </>
            ) : <td className="atl-none" colSpan={LEARNING_COLUMN_COUNT}/>}
            <td className="atl-actions">{renderInvitationOperation(invitation)}</td>
        </tr>
    );
}
